import React, { useEffect, useState } from "react";
import { Battery, ArrowLeft, Router, Camera as CameraIcon } from "lucide-react";
import {
  IQWatchDeviceWithData,
  IQWatchDeviceHistoryResponse,
  RouterStatus,
  Camera,
  CameraStatus,
  CameraSnapshot,
} from "../../types";
import {
  getIQWatchDeviceHistory,
  getRouterStatus,
} from "../../services/iqwatchService.ts";
import { cameraService } from "../../services/cameraService.ts";
import BatterySolarTab from "./DeviceDetailTabs/BatterySolarTab.tsx";
import RouterStatusTab from "./DeviceDetailTabs/RouterStatusTab.tsx";
import CameraStatusTab from "./DeviceDetailTabs/CameraStatusTab.tsx";

interface IQWatchDeviceDetailProps {
  deviceWithData: IQWatchDeviceWithData;
  onBack: () => void;
  initialTab?: "battery" | "router" | "cameras";
}

type TimeRange = "24h" | "7d" | "1m";

const IQWatchDeviceDetail: React.FC<IQWatchDeviceDetailProps> = ({
  deviceWithData,
  onBack,
  initialTab,
}) => {
  const [historyData, setHistoryData] =
    useState<IQWatchDeviceHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>("24h");
  const [routerStatus, setRouterStatus] = useState<RouterStatus | null>(null);
  const [loadingRouter, setLoadingRouter] = useState(false);
  const [activeTab, setActiveTab] = useState<"battery" | "router" | "cameras">(
    initialTab || "battery"
  );

  // Update activeTab when initialTab changes (e.g., when device changes)
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // Camera state
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [cameraStatuses, setCameraStatuses] = useState<
    Record<string, { camera_status: CameraStatus }>
  >({});
  const [cameraSnapshots, setCameraSnapshots] = useState<
    Record<string, { snapshot: CameraSnapshot }>
  >({});
  const [cameraErrors, setCameraErrors] = useState<
    Record<string, { statusError?: string; snapshotError?: string }>
  >({});
  const [loadingCameras, setLoadingCameras] = useState(false);

  const device = deviceWithData.device;
  const latestData = deviceWithData.latest_data;

  useEffect(() => {
    const fetchHistoryData = async () => {
      setLoading(true);
      setError(null);
      try {
        const history = await getIQWatchDeviceHistory(
          device.iqwatch_device_id,
          timeRange
        );
        setHistoryData(history);
      } catch (e) {
        console.error("Failed to fetch device history:", e);
        setError("Failed to load device history data");
      } finally {
        setLoading(false);
      }
    };

    fetchHistoryData();
  }, [device.iqwatch_device_id, timeRange]);

  // Fetch router status when device has router_serial_number
  useEffect(() => {
    const fetchRouterData = async () => {
      if (!device.router_serial_number) {
        setRouterStatus(null);
        return;
      }

      setLoadingRouter(true);
      try {
        // Fetch router status
        const status = await getRouterStatus({
          serial: device.router_serial_number,
        });
        setRouterStatus(status);
      } catch (error) {
        console.error("Failed to fetch router data:", error);
        // Don't set error state, just log it
      } finally {
        setLoadingRouter(false);
      }
    };

    fetchRouterData();
  }, [device.router_serial_number]);

  // Fetch camera data when cameras tab is active
  useEffect(() => {
    const fetchCameraData = async () => {
      if (activeTab !== "cameras") return;

      setLoadingCameras(true);
      try {
        // Fetch cameras list
        const camerasResponse = await cameraService.getCameras(device.id);
        const camerasList = camerasResponse.cameras || [];
        setCameras(camerasList);

        // Fetch status and snapshots for each camera incrementally
        const statusPromises: Promise<void>[] = [];
        const snapshotPromises: Promise<void>[] = [];

        // Clear previous errors
        setCameraErrors({});

        camerasList.forEach((camera) => {
          // Fetch status
          const statusPromise = cameraService
            .getCameraStatus(device.id, camera.id)
            .then((statusResponse) => {
              setCameraStatuses((prev) => ({
                ...prev,
                [camera.id]: statusResponse,
              }));
              // Clear status error on success
              setCameraErrors((prev) => {
                const updated = { ...prev };
                if (updated[camera.id]) {
                  delete updated[camera.id].statusError;
                  if (Object.keys(updated[camera.id]).length === 0) {
                    delete updated[camera.id];
                  }
                }
                return updated;
              });
            })
            .catch((error) => {
              console.error(
                `Failed to fetch status for camera ${camera.id}:`,
                error
              );
              // Extract error message
              const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                "Failed to fetch camera status";
              setCameraErrors((prev) => ({
                ...prev,
                [camera.id]: {
                  ...prev[camera.id],
                  statusError: errorMessage,
                },
              }));
            });
          statusPromises.push(statusPromise);

          // Fetch snapshot
          const snapshotPromise = cameraService
            .getCameraSnapshot(device.id, camera.id)
            .then((snapshotResponse) => {
              setCameraSnapshots((prev) => {
                const updated = {
                  ...prev,
                  [camera.id]: snapshotResponse,
                };
                return updated;
              });
              // Clear snapshot error on success
              setCameraErrors((prev) => {
                const updated = { ...prev };
                if (updated[camera.id]) {
                  delete updated[camera.id].snapshotError;
                  if (Object.keys(updated[camera.id]).length === 0) {
                    delete updated[camera.id];
                  }
                }
                return updated;
              });
            })
            .catch((error) => {
              console.error(
                `Failed to fetch snapshot for camera ${camera.id}:`,
                error
              );
              // Extract error message
              const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                "Failed to fetch camera snapshot";
              setCameraErrors((prev) => ({
                ...prev,
                [camera.id]: {
                  ...prev[camera.id],
                  snapshotError: errorMessage,
                },
              }));
            });
          snapshotPromises.push(snapshotPromise);
        });

        // Wait for all promises to complete (but allow individual failures)
        await Promise.allSettled([...statusPromises, ...snapshotPromises]);
      } catch (error) {
        console.error("Failed to fetch camera data:", error);
      } finally {
        setLoadingCameras(false);
      }
    };

    fetchCameraData();
  }, [activeTab, device.id]);

  // Refresh snapshot for a specific camera
  const handleRefreshSnapshot = async (cameraId: string) => {
    try {
      const snapshotResponse = await cameraService.getCameraSnapshot(
        device.id,
        cameraId
      );
      setCameraSnapshots((prev) => ({
        ...prev,
        [cameraId]: snapshotResponse,
      }));
      // Clear snapshot error on success
      setCameraErrors((prev) => {
        const updated = { ...prev };
        if (updated[cameraId]) {
          delete updated[cameraId].snapshotError;
          if (Object.keys(updated[cameraId]).length === 0) {
            delete updated[cameraId];
          }
        }
        return updated;
      });
    } catch (error) {
      console.error(
        `Failed to refresh snapshot for camera ${cameraId}:`,
        error
      );
      // Extract error message
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch camera snapshot";
      setCameraErrors((prev) => ({
        ...prev,
        [cameraId]: {
          ...prev[cameraId],
          snapshotError: errorMessage,
        },
      }));
    }
  };

  const formatDateTime = (dateTime: string | null | undefined) => {
    if (!dateTime) return "N/A";
    try {
      return new Date(dateTime).toLocaleString();
    } catch {
      return "N/A";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "running":
        return "text-green-600";
      case "stopped":
        return "text-yellow-600";
      case "error":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Devices</span>
          </button>
        </div>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading device details...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Devices</span>
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{device.name}</h2>
            <p className="text-gray-600">ID: {device.iqwatch_device_id}</p>
            <p className="text-sm text-gray-500">
              MAC: {device.iqwatch_mac_address}
            </p>
            <p className="text-sm text-gray-500">
              Last data: {formatDateTime(device.last_data_timestamp)}
            </p>
            {device.timezone && (
              <p className="text-sm text-gray-500">
                Timezone: {device.timezone}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
              latestData?.status || device.status
            )}`}
          >
            <div
              className={`w-2 h-2 rounded-full mr-2 ${
                (latestData?.status || device.status) === "running"
                  ? "bg-green-500"
                  : (latestData?.status || device.status) === "stopped"
                  ? "bg-yellow-500"
                  : "bg-red-500"
              }`}
            />
            {latestData?.status || device.status}
          </span>

          {/* Time Range Selector - Only show for Battery & Solar tab */}
          {activeTab === "battery" && (
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setTimeRange("24h")}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  timeRange === "24h"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <span>24H</span>
              </button>
              <button
                onClick={() => setTimeRange("7d")}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  timeRange === "7d"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <span>7D</span>
              </button>
              <button
                onClick={() => setTimeRange("1m")}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  timeRange === "1m"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <span>1M</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setActiveTab("battery")}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm transition-colors
              ${
                activeTab === "battery"
                  ? "border-green-500 text-green-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }
            `}
          >
            <div className="flex items-center space-x-2">
              <Battery className="w-5 h-5" />
              <span>Battery & Solar</span>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("router")}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm transition-colors
              ${
                activeTab === "router"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }
            `}
            disabled={!routerStatus && !latestData?.router_data}
          >
            <div className="flex items-center space-x-2">
              <Router className="w-5 h-5" />
              <span>Router Status</span>
              {!routerStatus && !latestData?.router_data && (
                <span className="text-xs text-gray-400">(No Router)</span>
              )}
            </div>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("cameras")}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm transition-colors
              ${
                activeTab === "cameras"
                  ? "border-purple-500 text-purple-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }
            `}
          >
            <div className="flex items-center space-x-2">
              <CameraIcon className="w-5 h-5" />
              <span>Cameras</span>
              {cameras.length > 0 && (
                <span className="text-xs text-gray-400">
                  ({cameras.length})
                </span>
              )}
            </div>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "battery" && (
        <BatterySolarTab
          deviceWithData={deviceWithData}
          historyData={historyData}
          timeRange={timeRange}
          error={error}
        />
      )}

      {activeTab === "router" && (
        <RouterStatusTab
          deviceWithData={deviceWithData}
          routerStatus={routerStatus}
          loadingRouter={loadingRouter}
        />
      )}

      {activeTab === "cameras" && (
        <CameraStatusTab
          cameras={cameras}
          cameraStatuses={cameraStatuses}
          cameraSnapshots={cameraSnapshots}
          cameraErrors={cameraErrors}
          loadingCameras={loadingCameras}
          onRefreshSnapshot={handleRefreshSnapshot}
        />
      )}
    </div>
  );
};

export default IQWatchDeviceDetail;
